import { OptionProps } from '@mui/base';
import dayjs from 'dayjs';
import { MobileOpCode, Visitor } from 'graphql/generated/graphqlTypes';
import useAccessEventsFilters from 'hooks/useAccessEventsFilters/useAccessEventsFilters';
import useDoors from 'hooks/useDoors/useDoors';
import useDrawer from 'hooks/useDrawer/useDrawer';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import useTranslation, { Translation } from 'hooks/useTranslation/useTranslation';
import useVisitorsGroupsMembers from 'hooks/useVisitorsGroupsMembers/useVisitorsGroupsMembers';
import { isEqual, sortBy, uniqWith } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { AccessEventsFiltersFormDrawer, DoorsState } from 'state/types';
import DrawerTemplate from 'ui/organisms/Drawer/DrawerTemplate';
import AccessEventsFiltersUI from 'ui/organisms/Drawer/variants/AccessEventsFilters';
import { createActionSelectOptions } from './actionSelectOptions';
import { createReasonSelectOptions } from './reasonSelectOptions';

const getVisitorOptionProp = (members: Visitor[], visitorId: string) => ({
  value: visitorId,
  label: members.find((visitor) => visitor.id === visitorId)?.name
});

const getLockOptionProp = (doors: DoorsState, doorIds: string) => ({
  value: doorIds,
  label: doors.find((door) => door.id === doorIds)?.name
});

const getActionOptionProp = (action: MobileOpCode, translation: Translation) => ({
  value: action,
  label: createActionSelectOptions(translation).find((item) => item.value === action)?.label
});

const getReasonOptionProp = (reason: string, translation: Translation) => ({
  value: reason,
  label: createReasonSelectOptions(translation).find((item) => item.value === reason)?.label
});

const AccessEventsFilters: React.FC<React.PropsWithChildren> = () => {
  const { translation } = useTranslation();
  const { register, handleSubmit, control } = useForm<FieldValues>();
  const { hideDrawer } = useDrawer<AccessEventsFiltersFormDrawer>();
  const enqueueSnackbar = useEnqueueSnackbar();
  const handleFetchError = (errorMessage: string) => enqueueSnackbar(errorMessage, { snackbartype: 'error' });
  const { doors, loading: doorsLoading } = useDoors({ handleFetchError });
  const { visitorsGroupsMembers, loading: visitorGroupsLoading } = useVisitorsGroupsMembers({ handleFetchError });
  const getVisitorGroupsMembers = useCallback(
    () =>
      sortBy(uniqWith(visitorsGroupsMembers.map((group) => group.members).flat(), isEqual), (member) =>
        member.name.toLowerCase()
      ),
    [visitorsGroupsMembers]
  );
  const {
    accessEventFilters,
    addFilter,
    addDataTimeCustomAccessEventFilter,
    removeDateTimeFilter,
    removeLockFilter,
    removeVisitorFilter,
    removeActionFilter,
    removeReasonFilter,
    applyFilters,
    isFilterApply
  } = useAccessEventsFilters();
  const [startDateTimeValue, setStartDateTimeValue] = useState<dayjs.Dayjs | null>(
    accessEventFilters.appliedFilters.dateRange?.rangeStart
      ? dayjs(accessEventFilters.appliedFilters.dateRange?.rangeStart)
      : null
  );
  const [endDateTimeValue, setEndDateTimeValue] = useState<dayjs.Dayjs | null>(
    accessEventFilters.appliedFilters.dateRange?.rangeEnd
      ? dayjs(accessEventFilters.appliedFilters.dateRange?.rangeEnd)
      : null
  );
  const [visitorSelectValue, setVisitorSelectValue] = useState<null | OptionProps<null | string>>(
    accessEventFilters.appliedFilters.visitorIds
      ? getVisitorOptionProp(getVisitorGroupsMembers(), accessEventFilters.appliedFilters.visitorIds[0])
      : null
  );
  const [lockSelectValue, setLockSelectValue] = useState<null | OptionProps<null | string>>(
    accessEventFilters.appliedFilters.doorIds
      ? getLockOptionProp(doors, accessEventFilters.appliedFilters.doorIds[0])
      : null
  );

  const [actionSelectValue, setActionSelectValue] = useState<null | OptionProps<null | MobileOpCode>>(
    accessEventFilters.appliedFilters.actions && accessEventFilters.appliedFilters.actions[0]
      ? getActionOptionProp(accessEventFilters.appliedFilters.actions[0], translation)
      : null
  );

  const [reasonSelectValue, setReasonSelectValue] = useState<null | OptionProps<null | string>>(
    accessEventFilters.appliedFilters.reason
      ? getReasonOptionProp(accessEventFilters.appliedFilters.reason, translation)
      : null
  );
  const [dataTimeRangeInputValidation, setDataTimeRangeInputValidation] = useState<string | undefined>(undefined);
  const [showValidation, setShowValidation] = useState(false);

  const handleStartDataTimeInputChange = (value: dayjs.Dayjs | null) => {
    if (value) {
      const parsedDateTime = value;
      if (parsedDateTime.isValid()) {
        setStartDateTimeValue(parsedDateTime);
        if (endDateTimeValue) {
          addDataTimeCustomAccessEventFilter({
            rangeStart: parsedDateTime.utc().format(),
            rangeEnd: endDateTimeValue.utc().format()
          });
        } else {
          const endDate = dayjs();
          setEndDateTimeValue(endDate);
          addDataTimeCustomAccessEventFilter({
            rangeStart: parsedDateTime.utc().format(),
            rangeEnd: endDate.utc().format()
          });
        }
      }
    }
  };

  const handleEndDataTimeInputChange = (value: dayjs.Dayjs | null) => {
    if (value) {
      const parsedDateTime = value;
      if (parsedDateTime.isValid()) {
        setEndDateTimeValue(parsedDateTime);
        if (startDateTimeValue) {
          addDataTimeCustomAccessEventFilter({
            rangeStart: startDateTimeValue.utc().format(),
            rangeEnd: parsedDateTime.utc().format()
          });
        } else {
          const startDate = dayjs('1900-01-01');
          setStartDateTimeValue(startDate);
          addDataTimeCustomAccessEventFilter({
            rangeStart: startDate.utc().format(),
            rangeEnd: parsedDateTime.utc().format()
          });
        }
      }
    }
  };

  const handleVisitorSelectChange = (visitor: null | OptionProps<null | string>) => {
    if (visitor && visitor.value) {
      addFilter({
        visitorIds: [visitor.value]
      });
      setVisitorSelectValue(visitor);
    } else {
      setVisitorSelectValue(null);
      removeVisitorFilter();
    }
  };

  const handleLockSelectChange = (lock: null | OptionProps<null | string>) => {
    if (lock && lock.value) {
      addFilter({
        doorIds: [lock.value]
      });
      setLockSelectValue(lock);
    } else {
      setLockSelectValue(null);
      removeLockFilter();
    }
  };

  const handleActionSelectChange = (action: null | OptionProps<null | MobileOpCode>) => {
    if (action && action.value) {
      addFilter({
        actions: [action.value]
      });
      setActionSelectValue(action);
    } else {
      setActionSelectValue(null);
      removeActionFilter();
    }
  };

  const handleReasonSelectChange = (reason: null | OptionProps<null | string>) => {
    if (reason && reason.value) {
      addFilter({
        reason: reason.value
      });
      setReasonSelectValue(reason);
    } else {
      setReasonSelectValue(null);
      removeReasonFilter();
    }
  };
  const handleClearFilters = () => {
    setStartDateTimeValue(null);
    setEndDateTimeValue(null);
    removeDateTimeFilter();

    setVisitorSelectValue(null);
    removeVisitorFilter();

    setLockSelectValue(null);
    removeLockFilter();

    setActionSelectValue(null);
    removeActionFilter();

    setReasonSelectValue(null);
    removeReasonFilter();
  };

  const onSubmit = async () => {
    applyFilters();
    hideDrawer();
  };

  const handleCloseDrawer = () => {
    removeDateTimeFilter();
    removeLockFilter();
    removeVisitorFilter();
    removeActionFilter();
    removeReasonFilter();
  };

  useEffect(() => {
    if (endDateTimeValue?.isBefore(startDateTimeValue)) {
      setShowValidation(true);
      setDataTimeRangeInputValidation(translation.start_time_cannot_be_after_end_time);
    } else {
      setShowValidation(false);
      setDataTimeRangeInputValidation(undefined);
    }
  }, [endDateTimeValue, startDateTimeValue, translation.start_time_cannot_be_after_end_time]);

  return (
    <DrawerTemplate
      title={translation.filters}
      confirmButtonText={translation.apply_filter}
      disableConfirmButton={
        !isFilterApply() &&
        !(
          Boolean(
            !(
              typeof accessEventFilters.filters.dateRange === 'undefined' ||
              accessEventFilters.filters.dateRange === null
            )
          ) ||
          Boolean(accessEventFilters.filters.doorIds) ||
          Boolean(accessEventFilters.filters.visitorIds) ||
          Boolean(accessEventFilters.filters.actions) ||
          Boolean(accessEventFilters.filters.reason)
        )
      }
      handleCloseDrawer={handleCloseDrawer}
      id="drawer"
      onSubmit={handleSubmit(onSubmit)}
    >
      <AccessEventsFiltersUI
        register={register}
        control={control}
        isClearButtonDisabled={
          Boolean(
            !(
              typeof accessEventFilters.filters.dateRange === 'undefined' ||
              accessEventFilters.filters.dateRange === null
            )
          ) ||
          Boolean(accessEventFilters.filters.doorIds) ||
          Boolean(accessEventFilters.filters.visitorIds) ||
          Boolean(accessEventFilters.filters.actions) ||
          Boolean(accessEventFilters.filters.reason)
        }
        handleClearFiltersButton={handleClearFilters}
        startDateTimeValue={startDateTimeValue}
        handleStartDataTimeInputChange={handleStartDataTimeInputChange}
        endDateTimeValue={endDateTimeValue}
        handleEndDataTimeInputChange={handleEndDataTimeInputChange}
        showValidation={showValidation}
        dataTimeRangeInputValidation={dataTimeRangeInputValidation}
        visitorSelectOptions={
          visitorGroupsLoading
            ? []
            : sortBy(getVisitorGroupsMembers(), (member) => member.name.toLocaleLowerCase()).map((member) => ({
                value: member.id,
                label: member.name
              }))
        }
        visitorSelectValue={visitorSelectValue}
        handleVisitorSelectChange={handleVisitorSelectChange}
        lockSelectOptions={
          doorsLoading
            ? []
            : sortBy(doors, (door) => door.name.toLocaleLowerCase()).map((door) => ({
                value: door.id,
                label: door.name
              }))
        }
        lockSelectValue={lockSelectValue}
        handleLockSelectChange={handleLockSelectChange}
        actionSelectOptions={sortBy(createActionSelectOptions(translation), (option) => option)}
        actionSelectValue={actionSelectValue}
        handleActionSelectChange={handleActionSelectChange}
        reasonSelectOptions={sortBy(createReasonSelectOptions(translation), (option) => option)}
        reasonSelectValue={reasonSelectValue}
        handleReasonSelectChange={handleReasonSelectChange}
      />
    </DrawerTemplate>
  );
};
export default AccessEventsFilters;
